// @flow
import lodashGet from 'lodash-es/get';
import { createDuck } from '@rover/react-lib/lib/redux-duck';
import petFactory from '@rover/react-lib/factories/petFactory';
import type {
  Pet,
  PetDetailsOptions,
  PetDetailForDisplay,
  Image,
  FetchPetImageResponse,
} from '@rover/react-lib/types';
import type {
  ActionCreator,
  SelectorCreator,
  EffectCreator,
  DuckOptions,
  Duck,
} from '@rover/react-lib/lib/redux-duck';
import { kebabifyString } from '@rover/utilities/object-utilities';
import {
  getPetDetailsFromPetInformation,
  getPetInformationKeys,
} from '@rover/react-lib/utils/pets';

import { selectors as currentCountryConfigurationSelectors } from '@rover/react-lib/redux/ducks/countryConfiguration.duck';
import { selectors as personSummarySelectors } from '@rover/react-lib/redux/ducks/personSummary.duck';

import {
  get,
  getDetail,
  getPetDetailsOptions,
  postOrPut,
  deleteImage,
  getImages,
} from '@rover/react-lib/apis/pet.api';

export type PetImages = {
  count: number,
  images: Array<Image>,
  next: ?string,
};

export type State = {
  pets: Array<Pet>,
  petInformation: Pet,
  petImages: PetImages,
  isLoading: boolean,
  isSaving: boolean,
  isLoadingImages: boolean,
  isDeletingImages: boolean,
  submittingCount: number,
  errorMessage: string,
  savePetErrorMessage: string,
  petDetailsOptions: PetDetailsOptions,
  isLoadingOptions: boolean,
};

type Actions = {
  fetchPetsPending: ActionCreator<void>,
  fetchPetsSuccess: ActionCreator<Array<Pet>>,
  fetchPetsFailure: ActionCreator<string>,
  savePetPending: ActionCreator<void>,
  savePetSuccess: ActionCreator<Pet>,
  savePetFailure: ActionCreator<string>,
  fetchPetInformationPending: ActionCreator<void>,
  fetchPetInformationSuccess: ActionCreator<Pet>,
  fetchPetInformationFailure: ActionCreator<string>,
  fetchPetImagesPending: ActionCreator<void>,
  fetchPetImagesSuccess: ActionCreator<FetchPetImageResponse>,
  fetchPetImagesFailure: ActionCreator<string>,
  fetchPetDetailsOptionsPending: ActionCreator<void>,
  fetchPetDetailsOptionsSuccess: ActionCreator<PetDetailsOptions>,
  fetchPetDetailsOptionsFailure: ActionCreator<string>,
  deletePetImagePending: ActionCreator<void>,
  deletePetImageSuccess: ActionCreator<string>,
  deletePetImageFailure: ActionCreator<string>,
  clearPets: ActionCreator<void>,
  setPetInformation: ActionCreator<Pet>,
};

type Effects = {
  fetchPets: EffectCreator<void>,
  fetchPetInformation: EffectCreator<string>,
  fetchPetImages: EffectCreator<{
    imageListUrl: string,
    page?: number,
  }>,
  fetchPetDetailsOptions: EffectCreator<void>,
  savePet: EffectCreator<Pet>,
  deleteImage: EffectCreator<string>,
};

type Selectors = {
  getPets: SelectorCreator<Array<Pet>>,
  getPetInformation: SelectorCreator<Pet>,
  getPetOpk: SelectorCreator<string>,
  getPetInformationBooleans: SelectorCreator<Array<PetDetailForDisplay>>,
  isPetInfoSubmitting: SelectorCreator<boolean>,
  isPetInfoSaving: SelectorCreator<boolean>,
  getPetsError: SelectorCreator<string>,
  getSavePetErrorMessage: SelectorCreator<string>,
  getPetImagesUrl: SelectorCreator<string>,
  getPetImages: SelectorCreator<PetImages>,
  getIsLoading: SelectorCreator<boolean>,
  getIsOwnPet: SelectorCreator<boolean>,
  getPetDetails: SelectorCreator<Array<PetDetailForDisplay>>,
  getPetDetailsOptions: SelectorCreator<PetDetailsOptions>,
  getPetDetailsOptionsLoading: SelectorCreator<boolean>,
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

const convertWeight = weightUnit => (pet: Pet): Pet => {
  let { weight } = pet;
  let unit = 'lbs';
  const weightObj = pet.weightObj.find(item => item.unit === weightUnit);
  weight = weightObj ? parseInt(weightObj.value, 10) : pet.weight;
  unit = weightObj ? weightObj.unit : 'lbs';
  return {
    ...pet,
    weight,
    unit,
  };
};

const createInitialState = () => ({
  pets: [],
  petInformation: petFactory(),
  petImages: {
    count: 0,
    images: [],
    next: null,
  },
  isLoading: false,
  isSaving: false,
  isDeletingImages: false,
  isLoadingImages: false,
  submittingCount: 0,
  errorMessage: '',
  savePetErrorMessage: '',
  petDetailsOptions: [],
  isLoadingOptions: false,
});

export const options: Options = {
  name: 'pets',
  initialState: createInitialState(),
  actions: {
    fetchPetsPending: () => state => ({
      ...state,
      isLoading: true,
    }),
    fetchPetsSuccess: pets => state => ({
      ...state,
      pets,
      isLoading: false,
      errorMessage: '',
    }),
    fetchPetsFailure: errorMessage => state => ({
      ...state,
      isLoading: false,
      errorMessage,
    }),
    savePetPending: () => state => ({
      ...state,
      submittingCount: state.submittingCount + 1,
      isSaving: true,
    }),
    savePetSuccess: savedPet => state => {
      const existingPet = state.pets.find(pet => pet.url === savedPet.url);
      const updatedPets = existingPet
        ? state.pets.map(pet => (pet.url === savedPet.url ? savedPet : pet))
        : [...state.pets, savedPet];
      return {
        ...state,
        submittingCount: state.submittingCount - 1,
        pets: updatedPets,
        savePetErrorMessage: '',
        isSaving: false,
      };
    },
    savePetFailure: message => state => ({
      ...state,
      submittingCount: state.submittingCount - 1,
      savePetErrorMessage: message,
      isSaving: false,
    }),
    fetchPetInformationPending: () => state => ({
      ...state,
      isLoading: true,
    }),
    fetchPetInformationSuccess: petInformation => state => ({
      ...state,
      petInformation,
      isLoading: false,
      errorMessage: '',
    }),
    fetchPetInformationFailure: errorMessage => state => ({
      ...state,
      isLoading: false,
      errorMessage,
    }),
    fetchPetImagesPending: () => state => ({
      ...state,
      isLoadingImages: true,
    }),
    fetchPetImagesSuccess: ({ results, previous, count, next }) => state => ({
      ...state,
      petImages: {
        count,
        // previous and next are urls that we can use to get the previous/next page of images.
        // when previous is null, it means that we are on the first page.
        // And we should clean up the existing images to avoid duplicating. Else if previous is defined,
        // we will append to existing images.
        images: previous ? [...state.petImages.images, ...results] : results,
        next,
      },
      isLoadingImages: false,
      errorMessage: '',
    }),
    fetchPetImagesFailure: errorMessage => state => ({
      ...state,
      isLoadingImages: false,
      errorMessage,
    }),
    fetchPetDetailsOptionsPending: () => state => ({
      ...state,
      isLoadingOptions: true,
    }),
    fetchPetDetailsOptionsSuccess: petDetailsOptions => state => ({
      ...state,
      petDetailsOptions,
      isLoadingOptions: false,
      errorMessage: '',
    }),
    fetchPetDetailsOptionsFailure: errorMessage => state => ({
      ...state,
      isLoadingOptions: false,
      errorMessage,
    }),
    deletePetImagePending: () => state => ({
      ...state,
      isDeletingImages: true,
    }),
    deletePetImageSuccess: imageUrl => state => ({
      ...state,
      petInformation:
        state.petInformation.defaultImage.url !== imageUrl
          ? state.petInformation
          : {
              ...state.petInformation,
              defaultImage: state.petImages.images[1] || petFactory().defaultImage,
            },
      petImages: {
        count: state.petImages.count - 1,
        images: state.petImages.images.filter(image => image.url !== imageUrl),
        next: state.petImages.next,
      },
      isDeletingImages: false,
      errorMessage: '',
    }),
    deletePetImageFailure: errorMessage => state => ({
      ...state,
      isDeletingImages: false,
      errorMessage,
    }),
    clearPets: () => state => {
      const emptyState = createInitialState();
      return {
        ...state,
        pets: emptyState.pets,
        petInformation: emptyState.petInformation,
        petImages: emptyState.petImages,
      };
    },
    setPetInformation: (petInformation: Pet) => state => ({
      ...state,
      petInformation,
    }),
  },
  effects: {
    fetchPets: () => async (
      dispatch,
      getState,
      { fetchPetsPending, fetchPetsSuccess, fetchPetsFailure },
      duckEffects,
      duckSelectors,
      { expressParams }
    ) => {
      dispatch(fetchPetsPending());
      try {
        const fetchPetsResponse: Array<Pet> = await get(expressParams);
        dispatch(fetchPetsSuccess(fetchPetsResponse));
      } catch (e) {
        dispatch(fetchPetsFailure(e.message));
      }
    },
    fetchPetInformation: petUrl => async (
      dispatch,
      getState,
      { fetchPetInformationPending, fetchPetInformationSuccess, fetchPetInformationFailure },
      duckEffects,
      duckSelectors,
      { expressParams }
    ) => {
      dispatch(fetchPetInformationPending());

      try {
        dispatch(fetchPetInformationSuccess(await getDetail(petUrl, expressParams)));
      } catch (e) {
        dispatch(fetchPetInformationFailure(e.message));
      }
    },
    fetchPetImages: ({ imageListUrl, page }) => async (
      dispatch,
      getState,
      { fetchPetImagesPending, fetchPetImagesSuccess, fetchPetImagesFailure },
      duckEffects,
      duckSelectors,
      { expressParams }
    ) => {
      dispatch(fetchPetImagesPending());

      try {
        dispatch(fetchPetImagesSuccess(await getImages(imageListUrl, page, expressParams)));
      } catch (e) {
        dispatch(fetchPetImagesFailure(e.message));
      }
    },
    fetchPetDetailsOptions: () => async (
      dispatch,
      getState,
      {
        fetchPetDetailsOptionsPending,
        fetchPetDetailsOptionsSuccess,
        fetchPetDetailsOptionsFailure,
      },
      duckEffects,
      duckSelectors,
      { expressParams }
    ) => {
      dispatch(fetchPetDetailsOptionsPending());

      try {
        dispatch(fetchPetDetailsOptionsSuccess(await getPetDetailsOptions(expressParams)));
      } catch (e) {
        dispatch(fetchPetDetailsOptionsFailure(e.message));
      }
    },
    deleteImage: imageUrl => async (
      dispatch,
      getState,
      { deletePetImagePending, deletePetImageSuccess, deletePetImageFailure },
      duckEffects,
      duckSelectors,
      { expressParams }
    ) => {
      dispatch(deletePetImagePending());

      try {
        await deleteImage(imageUrl, expressParams);
        dispatch(deletePetImageSuccess(imageUrl));
      } catch (e) {
        dispatch(deletePetImageFailure(e.message));
      }
    },
    savePet: pet => async (
      dispatch,
      getState,
      { savePetPending, savePetSuccess, savePetFailure },
      duckEffects,
      duckSelectors,
      { expressParams }
    ): Promise<?Pet> => {
      dispatch(savePetPending());

      try {
        const savedPet: Pet = await postOrPut(pet, expressParams);
        dispatch(savePetSuccess(savedPet));
        return savedPet;
      } catch (e) {
        dispatch(savePetFailure(e.message));
      }
      return null;
    },
  },
  selectors: {
    getPets: (getState, createSelector) =>
      createSelector(
        [getState, currentCountryConfigurationSelectors.getWeightLabel],
        (state, weightUnit) => state.pets.map(convertWeight(weightUnit))
      ),
    getPetInformation: (getState, createSelector) =>
      createSelector(
        [getState, currentCountryConfigurationSelectors.getWeightLabel],
        (state, weightUnit) => convertWeight(weightUnit)(state.petInformation)
      ),
    getPetOpk: (getState, createSelector, { getPetInformation }) =>
      createSelector(
        [getState, getPetInformation],
        (state, pet) => {
          const petOpk = pet.url.match(/pets\/([a-zA-z0-9]+)\/$/);
          return petOpk ? petOpk[1] : '';
        }
      ),
    getPetImagesUrl: (getState, createSelector, { getPetInformation }) =>
      createSelector(
        [getPetInformation],
        pet => lodashGet(pet, 'urls.images')
      ),
    getPetImages: (getState, createSelector) =>
      createSelector(
        [getState],
        state => state.petImages
      ),
    getIsOwnPet: (getState, createSelector, { getPetInformation }) =>
      createSelector(
        [getPetInformation, personSummarySelectors.getPersonUrl],
        (pet, personUrl) => pet.owner === personUrl
      ),
    getPetDetails: (getState, createSelector, { getPetInformation }) =>
      createSelector(
        [getState, getPetInformation],
        (state, pet) => {
          const { petDetails } = pet;
          const derivedPetDetails = getPetDetailsFromPetInformation(pet);

          return [...derivedPetDetails, ...(petDetails || [])];
        }
      ),
    getPetInformationBooleans: (getState, createSelector, { getPetInformation, getPetDetails }) =>
      createSelector(
        [getState, getPetInformation, getPetDetails],
        (state, pet, petDetails) => {
          const petInfoKeys = getPetInformationKeys(pet);

          return petDetails.filter(pd =>
            petInfoKeys.some(infoKey => kebabifyString(infoKey.key) === pd.slug)
          );
        }
      ),
    isPetInfoSubmitting: (getState, createSelector) =>
      createSelector(
        [getState],
        s => s.submittingCount > 0
      ),
    isPetInfoSaving: (getState, createSelector) =>
      createSelector(
        [getState],
        s => s.isSaving
      ),
    getPetsError: (getState, createSelector) =>
      createSelector(
        [getState],
        s => s.errorMessage
      ),
    getSavePetErrorMessage: (getState, createSelector) =>
      createSelector(
        [getState],
        s => s.savePetErrorMessage
      ),
    getIsLoading: (getState, createSelector) =>
      createSelector(
        [getState],
        s => s.isLoading
      ),
    getPetDetailsOptions: (getState, createSelector) =>
      createSelector(
        [getState],
        s => s.petDetailsOptions
      ),
    getPetDetailsOptionsLoading: (getState, createSelector) =>
      createSelector(
        [getState],
        s => s.isLoadingOptions
      ),
  },
};

export const { actions, selectors, effects, reducer } = (createDuck(options): Duck<
  State,
  Actions,
  Effects,
  Selectors
>);



// WEBPACK FOOTER //
// ../react-lib/src/redux/ducks/pets.duck.js